import type {FC} from 'react';

import type {BreadcrumbItem} from '@/modules/foundation/components/breadcrumb';
import type {CallToActionProps} from '@/modules/foundation/components/call-to-action/CallToAction';
import {imageWebFromImageWebSchema} from '@/modules/foundation/sanity/image/query';
import {linkFragmentToHref} from '@/modules/foundation/sanity/link/query';
import {PortableText} from '@/modules/foundation/sanity/portable-text';
import {
	HeroWithSideImage,
	heroWithSideImagePortableTextOverrides,
} from '@/modules/page/components/page-head/hero-with-side-image';

import type {HeroWithSideImageFragment} from './query';

interface Props {
	data: HeroWithSideImageFragment;
	breadcrumb?: BreadcrumbItem[];
}

export const HeroWithSideImageContainer: FC<Props> = ({data, breadcrumb}) => {
	const {image, heading, lead, theme, usePattern, showBreadcrumb} = data;

	let sideImage;

	if (image?.asset?._ref) {
		sideImage = imageWebFromImageWebSchema(image, 634 / 549);
	}

	let callToActionPrimary: CallToActionProps | undefined;
	let callToActionSecondary: CallToActionProps | undefined;

	if (data.callToActionPrimary) {
		callToActionPrimary = {
			href: linkFragmentToHref(data.callToActionPrimary),
			text: data.callToActionPrimary.text ?? '',
			targetBlank: data.callToActionPrimary.targetBlank,
			variant: 'default',
		};
	}

	if (data.callToActionSecondary) {
		callToActionSecondary = {
			href: linkFragmentToHref(data.callToActionSecondary),
			text: data.callToActionSecondary.text ?? '',
			targetBlank: data.callToActionSecondary.targetBlank,
			variant: theme === 'dark' ? 'secondaryDark' : 'secondary',
		};
	}

	return (
		<HeroWithSideImage
			imageWeb={sideImage}
			heading={heading}
			callToActionPrimary={callToActionPrimary}
			callToActionSecondary={callToActionSecondary}
			theme={theme}
			usePattern={usePattern}
			breadcrumb={breadcrumb}
			showBreadcrumb={showBreadcrumb}
		>
			{lead && lead.length > 0 ? (
				<PortableText
					value={lead}
					overrideClassNames={heroWithSideImagePortableTextOverrides}
					theme={theme}
				/>
			) : null}
		</HeroWithSideImage>
	);
};
